import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL = 8000; // 8 seconds
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    // Create a pleasant two-tone chime
    const frequencies = [830, 1050]; // Two ascending notes
    const durations = [0.15, 0.25];
    const delays = [0, 0.18];

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + delays[i]);
      gain.gain.setValueAtTime(0, now + delays[i]);
      gain.gain.linearRampToValueAtTime(0.3, now + delays[i] + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delays[i] + durations[i]);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + delays[i]);
      osc.stop(now + delays[i] + durations[i] + 0.05);
    });

    // Close context after sounds finish
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {
    // Audio not supported - fail silently
  }
}

export const NewLeadNotifier = ({ user }) => {
  const lastLeadIdRef = useRef(null);
  const initializedRef = useRef(false);
  const audioUnlockedRef = useRef(false);

  // Unlock audio on first user interaction (required by browsers)
  useEffect(() => {
    const unlock = () => {
      audioUnlockedRef.current = true;
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  const checkForNewLeads = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await fetch(`${API_URL}/api/leads/latest-timestamp`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;

      const data = await res.json();
      const currentLeadId = data.latestLeadId;

      if (!currentLeadId) return;

      // First poll: just store the current state, don't notify
      if (!initializedRef.current) {
        lastLeadIdRef.current = currentLeadId;
        initializedRef.current = true;
        return;
      }

      // If the latest lead ID changed, a new lead arrived
      if (currentLeadId !== lastLeadIdRef.current) {
        lastLeadIdRef.current = currentLeadId;

        // Play sound if audio is unlocked
        if (audioUnlockedRef.current) {
          playNotificationSound();
        }

        // Show toast notification
        const name = data.latestLeadName || 'Sin nombre';
        const source = data.latestSource || '';
        const sourceLabel = source === 'form_capture' ? ' (Formulario)'
          : source.toLowerCase().includes('whatsapp') ? ' (WhatsApp)'
          : source ? ` (${source})` : '';

        toast.info(`Nuevo lead: ${name}${sourceLabel}`, {
          duration: 8000,
          description: 'Se ha registrado un nuevo lead en el CRM'
        });
      }
    } catch {
      // Polling error - ignore silently
    }
  }, []);

  useEffect(() => {
    // Only for admin and comercial roles
    const role = (user?.role || '').toLowerCase();
    if (role !== 'admin' && role !== 'administrador' && role !== 'comercial') return;

    // Initial check
    checkForNewLeads();

    // Poll periodically
    const interval = setInterval(checkForNewLeads, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [user, checkForNewLeads]);

  // This component renders nothing - it's purely behavioral
  return null;
};
